import React, { useState, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Box,
  Divider,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Description as DocumentIcon,
  PlayArrow as StartIcon,
  Assignment as ChecklistIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const InstructionPanel = () => (
  <Card elevation={2} sx={{ width: '100%' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 6 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#1976d2', 
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Supporting Document Checklist
          </Typography>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
            <li>Vehicle ownership</li>
            <li>Void cheque</li>
            <li>Bill of sale</li>
            <li>Letter of Experience (LOE)</li>
            <li>Abstract letter</li>
            <li>Winter tire photos/receipts</li>
            <li>Vehicle photos (if required)</li>
          </ul>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DocumentSection = ({ title, files, onUpload, onDelete, onRename, sx, showNoteInput = false }) => {
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [note, setNote] = useState('');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = [...e.dataTransfer.files];
    if (files.length > 0) {
      if (showNoteInput) {
        setSelectedFile(files[0]);
        setShowNoteDialog(true);
      } else {
        onUpload(files[0]);
      }
    }
  }, [onUpload, showNoteInput]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (showNoteInput) {
        setSelectedFile(file);
        setShowNoteDialog(true);
      } else {
        onUpload(file);
      }
    }
  };

  const handleNoteSubmit = () => {
    if (selectedFile) {
      onUpload(selectedFile, note);
      setNote('');
      setSelectedFile(null);
      setShowNoteDialog(false);
    }
  };

  const startEditing = (fileId, currentName) => {
    setEditingId(fileId);
    setNewName(currentName);
  };

  const handleRename = (fileId) => {
    onRename(fileId, newName);
    setEditingId(null);
    setNewName('');
  };

  return (
    <>
      <Paper 
        elevation={2} 
        sx={{ 
          width: '100%',
          height: '100%',
          minHeight: '320px',
          display: 'flex',
          flexDirection: 'column',
          border: isDragging ? '2px dashed #2196f3' : '1px solid #e0e0e0',
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          backgroundColor: isDragging ? 'rgba(33, 150, 243, 0.04)' : '#fff',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: 3,
          },
          ...sx
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#1565c0',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '1.1rem'
            }}
          >
            <DocumentIcon />
            {title}
          </Typography>
        </Box>

        {/* Upload Area */}
        <Box
          sx={{
            p: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 56px)', // 减去header高度
            overflow: 'hidden'
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #90caf9',
              borderRadius: 2,
              backgroundColor: 'rgba(33, 150, 243, 0.02)',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              py: files.length > 0 ? 2 : 3,
              height: files.length > 0 ? '80px' : '140px',
              mb: 2,
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                borderColor: '#2196f3',
              },
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              onChange={handleFileUpload}
            />
            <CloudUploadIcon sx={{ 
              fontSize: files.length > 0 ? 28 : 40, 
              color: '#1976d2', 
              mb: files.length > 0 ? 0.5 : 1
            }} />
            <Typography 
              variant={files.length > 0 ? "body1" : "h6"} 
              sx={{ 
                color: '#1976d2', 
                fontWeight: 500, 
                mb: 0.5,
                fontSize: files.length > 0 ? '0.9rem' : '1rem',
                textAlign: 'center'
              }}
            >
              Drag & Drop files here
            </Typography>
            <Typography 
              variant={files.length > 0 ? "body2" : "body1"} 
              color="text.secondary"
              sx={{
                fontSize: files.length > 0 ? '0.8rem' : '0.9rem',
                textAlign: 'center'
              }}
            >
              or click to upload
            </Typography>
          </Box>

          {/* File List */}
          <List 
            sx={{ 
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              '& .MuiListItem-root': {
                py: 0.75,
                px: 1.5,
                mb: 1,
                '&:last-child': {
                  mb: 0
                }
              }
            }}
          >
            {files.map((file) => (
              <ListItem
                key={file.id}
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1
                }}
              >
                <Box sx={{ 
                  flex: 1,
                  minWidth: 0,
                  mr: 1
                }}>
                  {editingId === file.id ? (
                    <TextField
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      size="small"
                      fullWidth
                      variant="standard"
                      sx={{ 
                        fontSize: '0.9rem',
                        '& input': {
                          padding: '4px 0'
                        }
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: '#424242',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {file.name}
                    </Typography>
                  )}
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    gap: 0.5,
                    flexShrink: 0
                  }}
                >
                  {editingId === file.id ? (
                    <>
                      <IconButton 
                        size="small"
                        onClick={() => handleRename(file.id)}
                        sx={{ color: '#4caf50', p: 0.5 }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => setEditingId(null)}
                        sx={{ color: '#f44336', p: 0.5 }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton 
                        size="small"
                        onClick={() => startEditing(file.id, file.name)}
                        sx={{ color: '#1976d2', p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => onDelete(file.id)}
                        sx={{ color: '#f44336', p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Note Input Dialog */}
      <Dialog 
        open={showNoteDialog} 
        onClose={() => setShowNoteDialog(false)}
        PaperProps={{
          sx: {
            minWidth: '600px',
            maxWidth: '800px',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: '#f8f9fa', 
            color: '#1565c0',
            p: 3,
            pb: 2,
            fontSize: '1.5rem'
          }}
        >
          Add Note for {selectedFile?.name}
        </DialogTitle>
        <DialogContent 
          sx={{ 
            p: 4,
            pb: 5,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: '#666'
            }}
          >
            Please add a note to describe this supporting document:
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            placeholder="Enter your note here..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                fontSize: '1rem',
                lineHeight: '1.6'
              }
            }}
          />
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 3,
            backgroundColor: '#f8f9fa',
            gap: 2
          }}
        >
          <Button 
            onClick={() => setShowNoteDialog(false)}
            variant="outlined"
            sx={{
              px: 4,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleNoteSubmit}
            variant="contained"
            disabled={!note.trim()}
            sx={{
              px: 4,
              py: 1
            }}
          >
            Upload with Note
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DocumentUpload = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState({
    quote: [],
    application: [],
    mvr: [],
    autoPlus: [],
    supporting: [],
  });

  const handleUpload = (section) => (file, note = '') => {
    setDocuments(prev => ({
      ...prev,
      [section]: [...prev[section], {
        id: Date.now(),
        name: file.name,
        file: file,
        note: note
      }]
    }));
  };

  const handleDelete = (section) => (fileId) => {
    setDocuments(prev => ({
      ...prev,
      [section]: prev[section].filter(file => file.id !== fileId)
    }));
  };

  const handleRename = (section) => (fileId, newName) => {
    setDocuments(prev => ({
      ...prev,
      [section]: prev[section].map(file =>
        file.id === fileId ? { ...file, name: newName } : file
      )
    }));
  };

  const handleStartProcess = () => {
    navigate('/verify');
  };

  return (
    <Container maxWidth="xl" sx={{ minHeight: 'calc(100vh - 128px)', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Home
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Document Upload
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Upload and verify all required insurance documentation
        </Typography>

        {/* Start Process Button */}
        <Button
          variant="contained"
          startIcon={<StartIcon />}
          onClick={handleStartProcess}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          Start Process
        </Button>
      </Box>

      {/* Required Documents Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, backgroundColor: '#ffffff', mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#1565c0', fontWeight: 500 }}>
          Required Documents
        </Typography>
        
        <Grid container spacing={3} sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
          <Grid item xs={12} sm={6} lg={3}>
            <DocumentSection
              title="Quote"
              files={documents.quote}
              onUpload={handleUpload('quote')}
              onDelete={handleDelete('quote')}
              onRename={handleRename('quote')}
              sx={{ width: '100%', height: '100%', minHeight: '320px' }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <DocumentSection
              title="Application"
              files={documents.application}
              onUpload={handleUpload('application')}
              onDelete={handleDelete('application')}
              onRename={handleRename('application')}
              sx={{ width: '100%', height: '100%', minHeight: '320px' }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <DocumentSection
              title="MVR"
              files={documents.mvr}
              onUpload={handleUpload('mvr')}
              onDelete={handleDelete('mvr')}
              onRename={handleRename('mvr')}
              sx={{ width: '100%', height: '100%', minHeight: '320px' }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <DocumentSection
              title="Auto+"
              files={documents.autoPlus}
              onUpload={handleUpload('autoPlus')}
              onDelete={handleDelete('autoPlus')}
              onRename={handleRename('autoPlus')}
              sx={{ width: '100%', height: '100%', minHeight: '320px' }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Supporting Documents Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#1565c0', fontWeight: 500 }}>
          Supporting Documents
        </Typography>

        <Grid container spacing={3} sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
          {/* Upload Section */}
          <Grid item xs={12} md={8}>
            <DocumentSection
              title="Upload Supporting Documents"
              files={documents.supporting}
              onUpload={handleUpload('supporting')}
              onDelete={handleDelete('supporting')}
              onRename={handleRename('supporting')}
              showNoteInput={true}
              sx={{ 
                width: '100%',
                height: '100%',
                minHeight: '320px',
                '& .MuiPaper-root': {
                  height: '100%',
                  backgroundColor: '#f8f9fa',
                }
              }}
            />
          </Grid>

          {/* Checklist */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: { xs: 3, md: 0 } }}>
              <Typography variant="h6" sx={{ color: '#1976d2', mb: 2 }}>
                Required Documents:
              </Typography>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                <li>Vehicle ownership</li>
                <li>Void cheque</li>
                <li>Bill of sale</li>
                <li>Letter of Experience (LOE)</li>
                <li>Abstract letter</li>
                <li>Winter tire photos/receipts</li>
                <li>Vehicle photos (if required)</li>
              </ul>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default DocumentUpload;